import { usePermissions } from '../contexts/PermissionContext';

/**
 * Can Component - Conditional rendering based on permission
 *
 * Usage:
 * <Can permission="users.view">
 *   <Button>View Users</Button>
 * </Can>
 */
export const Can = ({ permission, children, fallback = null }) => {
  const { hasPermission } = usePermissions();

  // If no permission is specified, hide the element (more secure)
  if (!permission) {
    return fallback;
  }

  if (hasPermission(permission)) {
    return <>{children}</>;
  }

  return fallback;
};

/**
 * CanAny Component - Renders if user has ANY of the listed permissions
 *
 * Usage:
 * <CanAny permissions={['users.view', 'users.edit']}>
 *   <Button>Manage Users</Button>
 * </CanAny>
 */
export const CanAny = ({ permissions, children, fallback = null }) => {
  const { hasAnyPermission } = usePermissions();

  if (hasAnyPermission(permissions)) {
    return <>{children}</>;
  }

  return fallback;
};

/**
 * CanAll Component - Renders if user has ALL of the listed permissions
 *
 * Usage:
 * <CanAll permissions={['users.view', 'users.edit', 'users.delete']}>
 *   <Button>Full User Management</Button>
 * </CanAll>
 */
export const CanAll = ({ permissions, children, fallback = null }) => {
  const { hasAllPermissions } = usePermissions();

  if (hasAllPermissions(permissions)) {
    return <>{children}</>;
  }

  return fallback;
};

/**
 * HasRole Component - Conditional rendering based on role
 *
 * Usage:
 * <HasRole role="super_admin">
 *   <AdminPanel />
 * </HasRole>
 */
export const HasRole = ({ role, children, fallback = null }) => {
  const { hasRole } = usePermissions();

  if (hasRole(role)) {
    return <>{children}</>;
  }

  return fallback;
};

/**
 * HasAnyRole Component - Renders if user has ANY of the listed roles
 *
 * Usage:
 * <HasAnyRole roles={['super_admin', 'hr_manager']}>
 *   <HRPanel />
 * </HasAnyRole>
 */
export const HasAnyRole = ({ roles, children, fallback = null }) => {
  const { hasAnyRole } = usePermissions();

  if (hasAnyRole(roles)) {
    return <>{children}</>;
  }

  return fallback;
};

/**
 * HasAllRoles Component - Renders if user has ALL of the listed roles
 *
 * Usage:
 * <HasAllRoles roles={['hr_manager', 'recruiter']}>
 *   <SpecialPanel />
 * </HasAllRoles>
 */
export const HasAllRoles = ({ roles, children, fallback = null }) => {
  const { hasAllRoles } = usePermissions();

  if (hasAllRoles(roles)) {
    return <>{children}</>;
  }

  return fallback;
};

/**
 * IsSuperAdmin Component - Renders only for super admins
 *
 * Usage:
 * <IsSuperAdmin>
 *   <SuperAdminPanel />
 * </IsSuperAdmin>
 */
export const IsSuperAdmin = ({ children, fallback = null }) => {
  const { isSuperAdmin } = usePermissions();

  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  return fallback;
};

export default Can;
